import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import adminAxios from '../adminAxios'
import { API } from '../api'
import { authClient } from '../authClient'
import * as XLSX from 'xlsx'
import { AuditoriaAdmin } from '../components/AuditoriaAdmin'
import '../styles/admin.css'

const ADMIN_API = API.ADMIN

interface ImportPreview {
  fileName: string
  fileSize: string
  totalRows: number
  headersFound: string[]
  normalizedHeaders: string[]
  missingRequired: string[]
  missingParams: string[]
  sampleRows: any[]
  isValid: boolean
}

interface ImportResult {
  success: boolean
  message: string
  totalLidos: number
  criados: number
  atualizados: number
  erros: Array<{ linha: number; email: string; motivo: string }>
  turma: string
}

interface Turma {
  ID: number
  nome: string
  status: string
  dominios?: string
  perguntasEmbaralhadas?: number
}

interface Unidade {
  ID: number
  nome: string
  nivel: string
  superior: string
  dominio: string
}

interface Caracteristica {
  ID: number
  campo: string
  descricao: string
}

interface Manifestacao {
  ID: number
  campo: string
  descricao: string
}

interface Participante {
  ID: number
  nome: string
  email: string
  senha: string
  hierarquia: string
  ultimaAlteracaoEm?: string | null
  aceiteTermosEm?: string | null
  parametros?: Record<string, string>
}

interface TurmaParametro {
  id: number
  turma: number
  campo: string
  valores?: string[]
}

// Data/hora no fuso do navegador; '—' quando o participante ainda não alterou nada
const fmtData = (v?: string | null) => (v ? new Date(v).toLocaleString('pt-BR') : '—')

// Unidade exibida com o código (ID) junto do nome
const unidadeLabel = (u: Unidade) => `${u.ID} - ${u.nome}`

const PART_FORM_VAZIO = { id: '', nome: '', email: '', senha: '', hierarquia: '', parametros: {} as Record<string, string> }

const NOVO_VALOR = '__novo__'

// Caixa de seleção de um parâmetro de filtro: oferece os valores já cadastrados
// na turma e permite digitar um valor novo (máx. 255 caracteres, limite da coluna)
function ParametroSelect({ valores, value, onChange }: { valores: string[]; value: string; onChange: (v: string) => void }) {
  const opcoes = value && !valores.includes(value) ? [...valores, value].sort() : valores
  const [digitando, setDigitando] = useState(false)

  if (digitando) {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        <input className="adm-input" autoFocus maxLength={255} placeholder="Novo valor" value={value}
          onChange={(e) => onChange(e.target.value)} />
        <button type="button" className="adm-btn adm-btn-sm adm-btn-outline" title="Voltar à lista"
          onClick={() => setDigitando(false)}>↩</button>
      </div>
    )
  }
  return (
    <select className="adm-select" value={value}
      onChange={(e) => {
        if (e.target.value === NOVO_VALOR) { onChange(''); setDigitando(true) }
        else onChange(e.target.value)
      }}>
      <option value="">Selecione...</option>
      {opcoes.map(v => <option key={v} value={v}>{v}</option>)}
      <option value={NOVO_VALOR}>+ Novo valor...</option>
    </select>
  )
}

export function AdminPage() {
  const navigate = useNavigate()

  // Preferências de acessibilidade
  const [largeText, setLargeText] = useState(() => localStorage.getItem('a11y_large') === 'true')
  const [highContrast, setHighContrast] = useState(() => localStorage.getItem('a11y_contrast') === 'true')

  const toggleLargeText = () => {
    setLargeText(prev => {
      const next = !prev
      localStorage.setItem('a11y_large', String(next))
      return next
    })
  }

  const toggleHighContrast = () => {
    setHighContrast(prev => {
      const next = !prev
      localStorage.setItem('a11y_contrast', String(next))
      return next
    })
  }

  const [authenticated, setAuthenticated] = useState(false)
  const [authEmail, setAuthEmail] = useState('admin@capjota.local')
  const [authPassword, setAuthPassword] = useState('')
  const [authError, setAuthError] = useState('')

  useEffect(() => {
    if (sessionStorage.getItem('baToken')) setAuthenticated(true)
  }, [])

  const handleAuth = async () => {
    setAuthError('')
    try {
      const result = await authClient.signIn.email({
        email: authEmail,
        password: authPassword,
      })
      if (result.error) {
        setAuthError('Credenciais inválidas')
        return
      }
      // bearer plugin expõe o session-token no header set-auth-token
      const token = (result as { data?: { token?: string } }).data?.token
        ?? sessionStorage.getItem('baToken')
      if (token) {
        sessionStorage.setItem('baToken', token)
      }
      setAuthenticated(true)
    } catch (err) {
      console.error(err)
      setAuthError('Falha de conexão com o servidor. Tente novamente.')
    }
  }

  // ========== STATE ==========
  const [turmas, setTurmas] = useState<Turma[]>([])
  const [selectedTurma, setSelectedTurma] = useState('')
  const [turmaForm, setTurmaForm] = useState({ id: '', nome: '', status: '1', dominios: '', perguntasEmbaralhadas: '0' })

  const [unidades, setUnidades] = useState<Unidade[]>([])
  const [unidadeForm, setUnidadeForm] = useState({ id: '', nome: '', nivel: '1', superior: '0', dominio: '' })

  const [caracteristicas, setCaracteristicas] = useState<Caracteristica[]>([])
  const [caracForm, setCaracForm] = useState({ id: '', descricao: '' })

  const [manifestacoes, setManifestacoes] = useState<Manifestacao[]>([])
  const [manifForm, setManifForm] = useState({ id: '', descricao: '', tipo: 'MF', ccNum: '' })

  const [participantes, setParticipantes] = useState<Participante[]>([])
  const [partForm, setPartForm] = useState(PART_FORM_VAZIO)

  const [parametros, setParametros] = useState<TurmaParametro[]>([])
  const [parametroForm, setParametroForm] = useState({ id: '', campo: '' })

  const [importFile, setImportFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const [importTurma, setImportTurma] = useState('')
  const [importParametros, setImportParametros] = useState<TurmaParametro[]>([])
  const [importUnidades, setImportUnidades] = useState<Unidade[]>([])
  const [filePreview, setFilePreview] = useState<ImportPreview | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)

  useEffect(() => {
    if (!importTurma) {
      setImportParametros([])
      setImportUnidades([])
      return
    }
    adminAxios.get(`${ADMIN_API}/turma-parametros?turmaId=${importTurma}`)
      .then(res => setImportParametros(res.data || []))
      .catch(() => setImportParametros([]))
    adminAxios.get(`${ADMIN_API}/unidades?turmaId=${importTurma}`)
      .then(res => setImportUnidades(res.data || []))
      .catch(() => setImportUnidades([]))
  }, [importTurma])

  const [toast, setToast] = useState('')
  const [toastError, setToastError] = useState(false)
  const [activeSection, setActiveSection] = useState('turma')

  // Redireciona para login se o token expirar (401)
  const handleApiError = (err: unknown) => {
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      sessionStorage.removeItem('baToken')
      setAuthenticated(false)
      setAuthError('Sessão expirada. Faça login novamente.')
    } else {
      console.error(err)
    }
  }

  // ========== FETCH ==========
  const fetchTurmas = useCallback(async () => {
    try { const res = await adminAxios.get(`${ADMIN_API}/turmas`); setTurmas(res.data) } catch (err) { handleApiError(err) }
  }, [])

  const fetchUnidades = useCallback(async (turmaId: string) => {
    if (!turmaId) return
    try { const res = await adminAxios.get(`${ADMIN_API}/unidades?turmaId=${turmaId}`); setUnidades(res.data) } catch (err) { handleApiError(err) }
  }, [])

  const fetchCaracteristicas = useCallback(async (turmaId: string) => {
    if (!turmaId) return
    try { const res = await adminAxios.get(`${ADMIN_API}/caracteristicas?turmaId=${turmaId}`); setCaracteristicas(res.data) } catch (err) { handleApiError(err) }
  }, [])

  const fetchManifestacoes = useCallback(async (turmaId: string) => {
    if (!turmaId) return
    try { const res = await adminAxios.get(`${ADMIN_API}/manifestacoes?turmaId=${turmaId}`); setManifestacoes(res.data) } catch (err) { handleApiError(err) }
  }, [])

  const fetchParticipantes = useCallback(async (turmaId: string) => {
    if (!turmaId) return
    try { const res = await adminAxios.get(`${ADMIN_API}/participantes?turmaId=${turmaId}`); setParticipantes(res.data) } catch (err) { handleApiError(err) }
  }, [])

  const fetchParametros = useCallback(async (turmaId: string) => {
    if (!turmaId) return
    try { const res = await adminAxios.get(`${ADMIN_API}/turma-parametros?turmaId=${turmaId}`); setParametros(res.data) } catch (err) { handleApiError(err) }
  }, [])

  useEffect(() => { if (authenticated) fetchTurmas() }, [fetchTurmas, authenticated])

  useEffect(() => {
    if (authenticated && selectedTurma) {
      fetchUnidades(selectedTurma)
      fetchCaracteristicas(selectedTurma)
      fetchManifestacoes(selectedTurma)
      fetchParticipantes(selectedTurma)
      fetchParametros(selectedTurma)
    }
  }, [authenticated, selectedTurma, fetchUnidades, fetchCaracteristicas, fetchManifestacoes, fetchParticipantes, fetchParametros])

  const showToast = (msg: string, error = false) => {
    setToast(msg); setToastError(error); setTimeout(() => setToast(''), 4000)
  }

  // ========== CRUD ==========
  const saveTurma = async () => {
    try {
      await adminAxios.post(`${ADMIN_API}/turmas`, turmaForm)
      fetchTurmas(); setTurmaForm({ id: '', nome: '', status: '1', dominios: '', perguntasEmbaralhadas: '0' }); showToast('Turma salva!')
    } catch (err) { handleApiError(err); showToast('Erro ao salvar turma!') }
  }

  const deleteTurma = async (id: number) => {
    if (!confirm('Excluir esta turma?')) return
    try {
      await adminAxios.delete(`${ADMIN_API}/turmas/${id}`)
      fetchTurmas()
      showToast('Turma excluída!')
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        showToast(err.response.data?.error ?? 'Não é possível excluir esta turma.', true)
      } else {
        handleApiError(err)
        showToast('Erro ao excluir turma!', true)
      }
    }
  }

  const saveUnidade = async () => {
    try {
      await adminAxios.post(`${ADMIN_API}/unidades`, { ...unidadeForm, turma: selectedTurma })
      fetchUnidades(selectedTurma); setUnidadeForm({ id: '', nome: '', nivel: '1', superior: '0', dominio: '' }); showToast('Unidade salva!')
    } catch (err) { handleApiError(err); showToast('Erro ao salvar unidade!') }
  }

  const deleteUnidade = async (id: number) => {
    if (!confirm('Excluir esta unidade?')) return
    try { await adminAxios.delete(`${ADMIN_API}/unidades/${id}`); fetchUnidades(selectedTurma); showToast('Unidade excluída!') }
    catch (err) { handleApiError(err); showToast('Erro ao excluir unidade!') }
  }

  const saveCaracteristica = async () => {
    if (!caracForm.descricao.trim()) { showToast('Informe a descrição!'); return }
    try {
      await adminAxios.post(`${ADMIN_API}/caracteristicas`, { ...caracForm, turmaId: selectedTurma })
      fetchCaracteristicas(selectedTurma); setCaracForm({ id: '', descricao: '' }); showToast('Característica salva!')
    } catch (err) { handleApiError(err); showToast('Erro ao salvar característica!') }
  }

  const deleteCaracteristica = async (id: number) => {
    if (!confirm('Excluir esta característica?')) return
    try { await adminAxios.delete(`${ADMIN_API}/caracteristicas/${id}`); fetchCaracteristicas(selectedTurma); showToast('Característica excluída!') }
    catch (err) { handleApiError(err); showToast('Erro ao excluir característica!') }
  }

  const saveManifestacao = async () => {
    if (!manifForm.descricao.trim() || !manifForm.ccNum) { showToast('Informe descrição e a característica (CC)!'); return }
    try {
      await adminAxios.post(`${ADMIN_API}/manifestacoes`, { ...manifForm, turmaId: selectedTurma })
      fetchManifestacoes(selectedTurma); setManifForm({ id: '', descricao: '', tipo: 'MF', ccNum: '' }); showToast('Manifestação salva!')
    } catch (err) { handleApiError(err); showToast('Erro ao salvar manifestação!') }
  }

  const deleteManifestacao = async (id: number) => {
    if (!confirm('Excluir esta manifestação?')) return
    try { await adminAxios.delete(`${ADMIN_API}/manifestacoes/${id}`); fetchManifestacoes(selectedTurma); showToast('Manifestação excluída!') }
    catch (err) { handleApiError(err); showToast('Erro ao excluir manifestação!') }
  }

  const saveParticipante = async () => {
    if (!partForm.nome.trim() || !partForm.email.trim()) { showToast('Nome e e-mail obrigatórios!'); return }
    try {
      await adminAxios.post(`${ADMIN_API}/participantes`, { ...partForm, turma: selectedTurma })
      fetchParticipantes(selectedTurma); fetchParametros(selectedTurma); setPartForm(PART_FORM_VAZIO); showToast('Participante salvo!')
    } catch (err) { handleApiError(err); showToast('Erro ao salvar participante!') }
  }

  const deleteParticipante = async (id: number) => {
    if (!confirm('Excluir este participante?')) return
    try { await adminAxios.delete(`${ADMIN_API}/participantes/${id}`); fetchParticipantes(selectedTurma); showToast('Participante excluído!') }
    catch (err) { handleApiError(err); showToast('Erro ao excluir participante!') }
  }

  const exportarParticipantesExcel = () => {
    if (!participantes.length) return
    const dados = participantes.map(p => {
      const unid = unidades.find(u => String(u.ID) === String(p.hierarquia))
      return {
        ID: p.ID,
        Nome: p.nome,
        Login: p.email,
        Senha: p.senha || '',
        Unidade: unid?.nome || p.hierarquia || '',
        'Última Alteração': p.ultimaAlteracaoEm ? fmtData(p.ultimaAlteracaoEm) : '',
        'Aceite dos Termos': p.aceiteTermosEm ? fmtData(p.aceiteTermosEm) : ''
      }
    })
    const ws = XLSX.utils.json_to_sheet(dados)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Participantes')
    XLSX.writeFile(wb, `Participantes_Turma_${selectedTurma}.xlsx`)
  }

  const saveParametro = async () => {
    if (!parametroForm.campo.trim()) { showToast('Informe o nome do campo!'); return }
    try {
      await adminAxios.post(`${ADMIN_API}/turma-parametros`, { id: parametroForm.id, turmaId: selectedTurma, campo: parametroForm.campo })
      fetchParametros(selectedTurma); setParametroForm({ id: '', campo: '' }); showToast('Parâmetro salvo!')
    } catch (err) { handleApiError(err); showToast('Erro ao salvar parâmetro!') }
  }

  const deleteParametro = async (id: number) => {
    if (!confirm('Excluir este parâmetro?')) return
    try { await adminAxios.delete(`${ADMIN_API}/turma-parametros/${id}`); fetchParametros(selectedTurma); showToast('Parâmetro excluído!') }
    catch (err) { handleApiError(err); showToast('Erro ao excluir parâmetro!') }
  }

  const handleFileChange = (file: File | null) => {
    setImportFile(file)
    setImportResult(null)
    if (!file) {
      setFilePreview(null)
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer)
        const wb = XLSX.read(data, { type: 'array' })
        const sheetName = wb.SheetNames[0]
        if (!sheetName || !wb.Sheets[sheetName]) {
          setFilePreview(null)
          return
        }
        const sheet = wb.Sheets[sheetName]
        // Ignora linhas totalmente vazias (ex.: linhas só formatadas do modelo)
        const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' })
          .filter((row: any) => Object.values(row).some(v => String(v ?? '').trim() !== ''))
        const rawHeaders = rows.length > 0 ? Object.keys(rows[0]) : []

        const norm = (s: string) => String(s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[\s\-_]+/g, '')
        const normalizedHeaders = rawHeaders.map(norm)

        const hasNome = normalizedHeaders.some(h => ['nome', 'name', 'participante', 'colaborador'].includes(h))
        const hasEmail = normalizedHeaders.some(h => ['login', 'usuario', 'email', 'correioeletronico', 'mail'].includes(h))

        const missingRequired: string[] = []
        if (!hasNome) missingRequired.push('Nome')
        if (!hasEmail) missingRequired.push('Login')

        const missingParams: string[] = []
        importParametros.forEach(p => {
          const pNorm = norm(p.campo)
          if (!normalizedHeaders.includes(pNorm)) {
            missingParams.push(p.campo)
          }
        })

        const sizeKb = (file.size / 1024).toFixed(1) + ' KB'
        const isValid = missingRequired.length === 0 && missingParams.length === 0 && rows.length > 0

        setFilePreview({
          fileName: file.name,
          fileSize: sizeKb,
          totalRows: rows.length,
          headersFound: rawHeaders,
          normalizedHeaders,
          missingRequired,
          missingParams,
          sampleRows: rows.slice(0, 3),
          isValid
        })
      } catch (err) {
        console.error('Erro ao ler planilha no preview:', err)
        setFilePreview(null)
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleImport = async () => {
    if (!importFile || !importTurma) { showToast('Selecione arquivo e turma!'); return }
    setImporting(true)
    setImportResult(null)
    try {
      const formData = new FormData()
      formData.append('file', importFile)
      formData.append('turma', importTurma)
      formData.append('turmaId', importTurma)
      const res = await adminAxios.post(`${API.IMPORTAR}/importar-usuarios`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      setImportResult(res.data)
      fetchParticipantes(importTurma)
      showToast('Importação processada com sucesso!')
    } catch (err: any) { 
      handleApiError(err)
      const data = err.response?.data
      const erroMsg = data?.error || 'Erro ao importar planilha!'
      showToast(erroMsg, true)
      // Mostra as linhas recusadas e o motivo de cada uma, mesmo quando nada foi importado
      if (Array.isArray(data?.erros) && data.erros.length > 0) {
        setImportResult({ success: false, message: erroMsg, totalLidos: data.totalLidos ?? data.erros.length, criados: 0, atualizados: 0, erros: data.erros, turma: importTurma })
      }
    } finally { 
      setImporting(false) 
    }
  }

  // Baixa o modelo de importação já com as colunas dos parâmetros de filtro
  // da turma selecionada (elas variam por turma e são exigidas na importação).
  const downloadModelo = async () => {
    try {
      const url = importTurma
        ? `${ADMIN_API}/modelo-importacao?turmaId=${importTurma}`
        : `${ADMIN_API}/modelo-importacao`
      const res = await adminAxios.get(url, { responseType: 'blob' })
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = 'modelo-importacao.xlsx'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch (err) { handleApiError(err); showToast('Erro ao baixar arquivo modelo!', true) }
  }

  // ========== helpers ==========
  // Seletor de turma exibido dentro de cada aba que depende de uma turma
  // (em vez de um único seletor fixo fora das abas, na barra lateral).
  const turmaSelector = (
    <div style={{ marginBottom: '20px', maxWidth: '440px' }}>
      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '13px', color: 'var(--adm-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Turma Ativa:
      </label>
      <select 
        value={selectedTurma} 
        onChange={(e) => setSelectedTurma(e.target.value)}
        className="adm-select"
      >
        <option value="">Selecione uma turma...</option>
        {turmas.map(t => <option key={t.ID} value={t.ID}>(código: {t.ID}) {t.nome.trim()}</option>)}
      </select>
    </div>
  )

  const parseCampo = (campo: string) => {
    const m = campo.match(/_CC(\d+)_(MF|MD)(\d+)$/)
    return m ? { ccNum: m[1], tipo: m[2], seq: m[3] } : null
  }

  const ccNumFromCampo = (campo: string) => {
    const m = campo.match(/_CC(\d+)$/)
    return m ? m[1] : '?'
  }

  // ========== AUTH ==========
  if (!authenticated) {
    return (
      <div className={`adm-page${largeText ? ' a11y-large' : ''}${highContrast ? ' a11y-contrast' : ''}`} style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100dvh', padding: 'clamp(16px, 4vw, 24px)', boxSizing: 'border-box'
      }}>
        <div className="adm-card" style={{ width: '100%', maxWidth: '420px', padding: 'clamp(20px, 5vw, 32px)', boxSizing: 'border-box' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <span className="adm-logo" style={{ color: 'var(--adm-navy)', fontSize: 'clamp(1.5rem, 5vw, 2rem)' }}>cap jota</span>
            <h2 style={{ margin: '8px 0 4px', color: 'var(--adm-navy)', fontSize: 'clamp(1.15rem, 4.5vw, 1.25rem)', fontWeight: 800 }}>
              Painel Administrativo
            </h2>
            <p style={{ margin: 0, color: 'var(--adm-muted)', fontSize: '14px', lineHeight: '1.4' }}>
              Acesso exclusivo para administradores do sistema
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div className="adm-field">
              <label>E-mail institucional</label>
              <input 
                type="email" 
                placeholder="admin@capjota.local" 
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                className="adm-input" 
              />
            </div>
            <div className="adm-field">
              <label>Senha de acesso</label>
              <input 
                type="password" 
                placeholder="Sua senha" 
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
                className="adm-input" 
              />
            </div>

            {authError && (
              <div role="alert" style={{
                background: 'var(--adm-err-soft)', color: 'var(--adm-err)', padding: '10px 14px',
                borderRadius: '6px', fontSize: '13.5px', fontWeight: 600, border: '1px solid rgba(153,27,27,0.2)'
              }}>
                {authError}
              </div>
            )}

            <button 
              type="button" 
              onClick={handleAuth} 
              className="adm-btn adm-btn-primary" 
              style={{ minHeight: '48px', fontSize: '1rem', marginTop: '6px' }}
            >
              Entrar no Painel
            </button>
          </div>
        </div>
      </div>
    )
  }

  const navLabels: Record<string, string> = {
    turma: 'Turmas', unidades: 'Unidades', caracteristicas: 'Características',
    manifestacoes: 'Manifestações', participantes: 'Participantes',
    parametros: 'Parâmetros de Filtro', import: 'Importar', auditoria: 'Auditoria'
  }

  // ========== RENDER ==========
  return (
    <div className={`adm-page${largeText ? ' a11y-large' : ''}${highContrast ? ' a11y-contrast' : ''}`}>
      <header className="adm-header">
        <div className="adm-header-row">
          <div className="adm-brand-group">
            <span className="adm-logo">cap jota</span>
            <span className="adm-sublogo">Painel Administrativo</span>
          </div>

          <div className="adm-a11y-tools" role="toolbar" aria-label="Ferramentas de Acessibilidade">
            <button 
              type="button" 
              className={`adm-a11y-btn${largeText ? ' active' : ''}`}
              onClick={toggleLargeText}
              title="Alternar tamanho de texto"
              aria-pressed={largeText}
            >
              <span>A+</span>
              <span>{largeText ? 'Texto Normal' : 'Texto Maior'}</span>
            </button>
            <button 
              type="button" 
              className={`adm-a11y-btn${highContrast ? ' active' : ''}`}
              onClick={toggleHighContrast}
              title="Alternar modo de alto contraste"
              aria-pressed={highContrast}
            >
              <span>🌓</span>
              <span>Contraste</span>
            </button>
            <button 
              type="button" 
              onClick={() => navigate('/menu')} 
              className="adm-btn-voltar"
            >
              ← Voltar ao Menu
            </button>
          </div>
        </div>
      </header>

      <div className="adm-corpo">
        {/* Sidebar no desktop, faixa de tabs no mobile */}
        <aside className="adm-menu">
          <nav aria-label="Seções Administrativas">
            {Object.entries(navLabels).map(([key, label]) => (
              <button 
                key={key} 
                onClick={() => { setActiveSection(key); setSelectedTurma(''); setImportTurma('') }} 
                className={`adm-nav-btn${activeSection === key ? ' active' : ''}`}
                aria-current={activeSection === key ? 'page' : undefined}
              >
                <span>{label}</span>
                {activeSection === key && <span aria-hidden="true">›</span>}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="adm-conteudo">

          {/* ===== TURMAS ===== */}
          {activeSection === 'turma' && (
            <div className="adm-card">
              <h2 className="adm-section-title">Gerenciamento de Turmas</h2>
              
              {!turmaForm.id && (
                <div className="adm-form-grid">
                  <div className="adm-field">
                    <label>Nome da Turma</label>
                    <input className="adm-input" placeholder="Ex: Turma Operações 2026" value={turmaForm.nome}
                      onChange={(e) => setTurmaForm({ ...turmaForm, nome: e.target.value })} />
                  </div>
                  <div className="adm-field">
                    <label>Domínios Permitidos</label>
                    <input className="adm-input" placeholder="ex: empresa.com; filial.com" value={turmaForm.dominios || ''}
                      onChange={(e) => setTurmaForm({ ...turmaForm, dominios: e.target.value })} />
                  </div>
                  <div className="adm-field">
                    <label>Status</label>
                    <select className="adm-select" value={String(turmaForm.status) === '1' ? '1' : '0'}
                      onChange={(e) => setTurmaForm({ ...turmaForm, status: e.target.value })}>
                      <option value="1">Aberta (Aceita respostas)</option>
                      <option value="0">Fechada (Bloqueada)</option>
                    </select>
                  </div>
                  <div className="adm-field">
                    <label>Ordem das Questões</label>
                    <select className="adm-select" value={turmaForm.perguntasEmbaralhadas}
                      onChange={(e) => setTurmaForm({ ...turmaForm, perguntasEmbaralhadas: e.target.value })}>
                      <option value="0">Por atributo (Pares funcionais)</option>
                      <option value="1">Embaralhadas (Determinístico)</option>
                    </select>
                  </div>
                  <button type="button" onClick={saveTurma} className="adm-btn adm-btn-success">
                    + Adicionar Turma
                  </button>
                </div>
              )}

              <div className="adm-table-wrapper">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>ID</th>
                      <th>Nome da Turma</th>
                      <th>Domínios</th>
                      <th style={{ width: '100px' }}>Status</th>
                      <th style={{ width: '130px' }}>Ordem</th>
                      <th style={{ width: '140px', textAlign: 'center' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {turmas.map(t => {
                      const aberta = String(t.status) === '1'
                      const editing = turmaForm.id === String(t.ID)
                      if (editing) {
                        return (
                          <tr key={t.ID} className="editing">
                            <td><b>{t.ID}</b></td>
                            <td>
                              <input className="adm-input" value={turmaForm.nome}
                                onChange={(e) => setTurmaForm({ ...turmaForm, nome: e.target.value })} />
                            </td>
                            <td>
                              <input className="adm-input" value={turmaForm.dominios || ''}
                                onChange={(e) => setTurmaForm({ ...turmaForm, dominios: e.target.value })} />
                            </td>
                            <td>
                              <select className="adm-select" value={String(turmaForm.status) === '1' ? '1' : '0'}
                                onChange={(e) => setTurmaForm({ ...turmaForm, status: e.target.value })}>
                                <option value="1">Aberta</option>
                                <option value="0">Fechada</option>
                              </select>
                            </td>
                            <td>
                              <select className="adm-select" value={turmaForm.perguntasEmbaralhadas}
                                onChange={(e) => setTurmaForm({ ...turmaForm, perguntasEmbaralhadas: e.target.value })}>
                                <option value="0">Por atributo</option>
                                <option value="1">Embaralhadas</option>
                              </select>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                <button type="button" onClick={saveTurma} className="adm-btn adm-btn-sm adm-btn-success">Salvar</button>
                                <button type="button" onClick={() => setTurmaForm({ id: '', nome: '', status: '1', dominios: '', perguntasEmbaralhadas: '0' })} className="adm-btn adm-btn-sm adm-btn-outline">Cancelar</button>
                              </div>
                            </td>
                          </tr>
                        )
                      }
                      return (
                        <tr key={t.ID}>
                          <td><b>{t.ID}</b></td>
                          <td><strong>{t.nome}</strong></td>
                          <td style={{ fontSize: '13px', color: 'var(--adm-muted)' }}>{t.dominios || '—'}</td>
                          <td>
                            <span className={`adm-badge ${aberta ? 'adm-badge-open' : 'adm-badge-closed'}`}>
                              {aberta ? 'Aberta' : 'Fechada'}
                            </span>
                          </td>
                          <td>
                            <span className={`adm-badge ${t.perguntasEmbaralhadas === 1 ? 'adm-badge-shuffled' : 'adm-badge-mode'}`}>
                              {t.perguntasEmbaralhadas === 1 ? 'Embaralhadas' : 'Por atributo'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                              <button type="button" onClick={() => setTurmaForm({ id: String(t.ID), nome: t.nome, status: String(t.status), dominios: t.dominios || '', perguntasEmbaralhadas: String(t.perguntasEmbaralhadas ?? 0) })} className="adm-btn adm-btn-sm adm-btn-outline">Editar</button>
                              <button type="button" onClick={() => deleteTurma(t.ID)} className="adm-btn adm-btn-sm adm-btn-danger">Excluir</button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ===== UNIDADES ===== */}
          {activeSection === 'unidades' && (
            <div className="adm-card">
              <h2 className="adm-section-title">Unidades Organizacionais</h2>
              {turmaSelector}
              {!selectedTurma && <p style={{ color: 'var(--adm-muted)' }}>Selecione uma turma para visualizar as unidades.</p>}
              {selectedTurma && (
                <>
                  {!unidadeForm.id && (
                    <div className="adm-form-grid">
                      <div className="adm-field">
                        <label>Nome da Unidade</label>
                        <input className="adm-input" placeholder="Ex: Operações SP" value={unidadeForm.nome}
                          onChange={(e) => setUnidadeForm({ ...unidadeForm, nome: e.target.value })} />
                      </div>
                      <div className="adm-field">
                        <label>Nível Hierárquico</label>
                        <input className="adm-input" placeholder="Ex: 1" value={unidadeForm.nivel}
                          onChange={(e) => setUnidadeForm({ ...unidadeForm, nivel: e.target.value })} />
                      </div>
                      <div className="adm-field">
                        <label>Superior (ID)</label>
                        <input className="adm-input" placeholder="0 para raiz" value={unidadeForm.superior}
                          onChange={(e) => setUnidadeForm({ ...unidadeForm, superior: e.target.value })} />
                      </div>
                      <button type="button" onClick={saveUnidade} className="adm-btn adm-btn-success">
                        + Adicionar Unidade
                      </button>
                    </div>
                  )}

                  <div className="adm-table-wrapper">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th style={{ width: '60px' }}>ID</th>
                          <th>Nome da Unidade</th>
                          <th style={{ width: '90px' }}>Nível</th>
                          <th style={{ width: '100px' }}>Superior</th>
                          <th style={{ width: '140px', textAlign: 'center' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unidades.map(u => {
                          const editing = unidadeForm.id === String(u.ID)
                          if (editing) {
                            return (
                              <tr key={u.ID} className="editing">
                                <td><b>{u.ID}</b></td>
                                <td><input className="adm-input" value={unidadeForm.nome}
                                  onChange={(e) => setUnidadeForm({ ...unidadeForm, nome: e.target.value })} /></td>
                                <td><input className="adm-input" value={unidadeForm.nivel}
                                  onChange={(e) => setUnidadeForm({ ...unidadeForm, nivel: e.target.value })} /></td>
                                <td><input className="adm-input" value={unidadeForm.superior}
                                  onChange={(e) => setUnidadeForm({ ...unidadeForm, superior: e.target.value })} /></td>
                                <td style={{ textAlign: 'center' }}>
                                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                    <button type="button" onClick={saveUnidade} className="adm-btn adm-btn-sm adm-btn-success">Salvar</button>
                                    <button type="button" onClick={() => setUnidadeForm({ id: '', nome: '', nivel: '1', superior: '0', dominio: '' })} className="adm-btn adm-btn-sm adm-btn-outline">Cancelar</button>
                                  </div>
                                </td>
                              </tr>
                            )
                          }
                          return (
                            <tr key={u.ID}>
                              <td><b>{u.ID}</b></td>
                              <td><strong>{u.nome}</strong></td>
                              <td>{u.nivel}</td>
                              <td>{u.superior}</td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                  <button type="button" onClick={() => setUnidadeForm({ id: String(u.ID), nome: u.nome, nivel: String(u.nivel), superior: String(u.superior), dominio: u.dominio || '' })} className="adm-btn adm-btn-sm adm-btn-outline">Editar</button>
                                  <button type="button" onClick={() => deleteUnidade(u.ID)} className="adm-btn adm-btn-sm adm-btn-danger">Excluir</button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                        {unidades.length === 0 && (
                          <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--adm-muted)', padding: '20px' }}>Nenhuma unidade cadastrada nesta turma.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== CARACTERÍSTICAS ===== */}
          {activeSection === 'caracteristicas' && (
            <div className="adm-card">
              <h2 className="adm-section-title">Características Culturais (CC)</h2>
              {turmaSelector}
              {!selectedTurma && <p style={{ color: 'var(--adm-muted)' }}>Selecione uma turma para visualizar as características.</p>}
              {selectedTurma && (
                <>
                  {!caracForm.id && (
                    <div className="adm-form-grid" style={{ gridTemplateColumns: '1fr auto' }}>
                      <div className="adm-field">
                        <label>Descrição da Característica</label>
                        <input className="adm-input" placeholder="Ex: Lideramos e influenciamos nosso ambiente..."
                          value={caracForm.descricao} onChange={(e) => setCaracForm({ ...caracForm, descricao: e.target.value })} />
                      </div>
                      <button type="button" onClick={saveCaracteristica} className="adm-btn adm-btn-success">
                        + Adicionar CC
                      </button>
                    </div>
                  )}

                  <div className="adm-table-wrapper">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th style={{ width: '60px', textAlign: 'center' }}>CC</th>
                          <th style={{ width: '120px' }}>Campo</th>
                          <th>Descrição da Característica</th>
                          <th style={{ width: '140px', textAlign: 'center' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {caracteristicas.map(c => {
                          const editing = caracForm.id === String(c.ID)
                          if (editing) {
                            return (
                              <tr key={c.ID} className="editing">
                                <td style={{ textAlign: 'center' }}><b>CC{ccNumFromCampo(c.campo)}</b></td>
                                <td style={{ fontSize: '12px', color: 'var(--adm-muted)' }}>{c.campo}</td>
                                <td>
                                  <input className="adm-input" value={caracForm.descricao}
                                    onChange={(e) => setCaracForm({ ...caracForm, descricao: e.target.value })} />
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                    <button type="button" onClick={saveCaracteristica} className="adm-btn adm-btn-sm adm-btn-success">Salvar</button>
                                    <button type="button" onClick={() => setCaracForm({ id: '', descricao: '' })} className="adm-btn adm-btn-sm adm-btn-outline">Cancelar</button>
                                  </div>
                                </td>
                              </tr>
                            )
                          }
                          return (
                            <tr key={c.ID}>
                              <td style={{ textAlign: 'center' }}><b style={{ color: 'var(--adm-navy)' }}>CC{ccNumFromCampo(c.campo)}</b></td>
                              <td style={{ fontSize: '12px', color: 'var(--adm-muted)' }}>{c.campo}</td>
                              <td><strong>{c.descricao}</strong></td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                  <button type="button" onClick={() => setCaracForm({ id: String(c.ID), descricao: c.descricao })} className="adm-btn adm-btn-sm adm-btn-outline">Editar</button>
                                  <button type="button" onClick={() => deleteCaracteristica(c.ID)} className="adm-btn adm-btn-sm adm-btn-danger">Excluir</button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                        {caracteristicas.length === 0 && (
                          <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--adm-muted)', padding: '20px' }}>Nenhuma característica cadastrada nesta turma.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== MANIFESTAÇÕES ===== */}
          {activeSection === 'manifestacoes' && (
            <div className="adm-card">
              <h2 className="adm-section-title">Manifestações (Comportamentos)</h2>
              {turmaSelector}
              {!selectedTurma && <p style={{ color: 'var(--adm-muted)' }}>Selecione uma turma para visualizar as manifestações.</p>}
              {selectedTurma && (
                <>
                  {!manifForm.id && (
                    <div className="adm-form-grid">
                      <div className="adm-field">
                        <label>Característica (CC)</label>
                        <select className="adm-select" value={manifForm.ccNum} onChange={(e) => setManifForm({ ...manifForm, ccNum: e.target.value })}>
                          <option value="">Selecione CC...</option>
                          {caracteristicas.map(c => (
                            <option key={c.ID} value={ccNumFromCampo(c.campo)}>
                              CC{ccNumFromCampo(c.campo)} — {c.descricao.substring(0, 36)}...
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="adm-field" style={{ maxWidth: '180px' }}>
                        <label>Tipo de Manifestação</label>
                        <select className="adm-select" value={manifForm.tipo} onChange={(e) => setManifForm({ ...manifForm, tipo: e.target.value })}>
                          <option value="MF">MF — Funcional</option>
                          <option value="MD">MD — Disfuncional</option>
                        </select>
                      </div>
                      <div className="adm-field" style={{ gridColumn: 'span 2' }}>
                        <label>Texto da Manifestação</label>
                        <input className="adm-input" placeholder="Descrição clara do comportamento observado"
                          value={manifForm.descricao} onChange={(e) => setManifForm({ ...manifForm, descricao: e.target.value })} />
                      </div>
                      <button type="button" onClick={saveManifestacao} className="adm-btn adm-btn-success">
                        + Adicionar Manifestação
                      </button>
                    </div>
                  )}

                  <div className="adm-table-wrapper">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th style={{ width: '60px', textAlign: 'center' }}>CC</th>
                          <th style={{ width: '90px', textAlign: 'center' }}>Tipo</th>
                          <th style={{ width: '60px', textAlign: 'center' }}>Seq</th>
                          <th>Descrição da Manifestação</th>
                          <th style={{ width: '140px', textAlign: 'center' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {manifestacoes
                          .sort((a, b) => a.campo.localeCompare(b.campo))
                          .map(m => {
                            const p = parseCampo(m.campo)
                            const editing = manifForm.id === String(m.ID)
                            if (editing) {
                              return (
                                <tr key={m.ID} className="editing">
                                  <td style={{ textAlign: 'center' }}><b>CC{p?.ccNum ?? '?'}</b></td>
                                  <td style={{ textAlign: 'center' }}>
                                    <span className={`adm-badge ${p?.tipo === 'MF' ? 'adm-badge-open' : 'adm-badge-closed'}`}>
                                      {p?.tipo ?? '?'}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'center' }}>{p?.seq ?? '?'}</td>
                                  <td>
                                    <input className="adm-input" value={manifForm.descricao}
                                      onChange={(e) => setManifForm({ ...manifForm, descricao: e.target.value })} />
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                      <button type="button" onClick={saveManifestacao} className="adm-btn adm-btn-sm adm-btn-success">Salvar</button>
                                      <button type="button" onClick={() => setManifForm({ id: '', descricao: '', tipo: 'MF', ccNum: '' })} className="adm-btn adm-btn-sm adm-btn-outline">Cancelar</button>
                                    </div>
                                  </td>
                                </tr>
                              )
                            }
                            return (
                              <tr key={m.ID}>
                                <td style={{ textAlign: 'center' }}><b style={{ color: 'var(--adm-navy)' }}>CC{p?.ccNum ?? '?'}</b></td>
                                <td style={{ textAlign: 'center' }}>
                                  <span className={`adm-badge ${p?.tipo === 'MF' ? 'adm-badge-open' : 'adm-badge-closed'}`}>
                                    {p?.tipo === 'MF' ? 'MF (Funcional)' : 'MD (Disfuncional)'}
                                  </span>
                                </td>
                                <td style={{ textAlign: 'center' }}>{p?.seq ?? '?'}</td>
                                <td>{m.descricao}</td>
                                <td style={{ textAlign: 'center' }}>
                                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                    <button type="button" onClick={() => setManifForm({ id: String(m.ID), descricao: m.descricao, tipo: p?.tipo ?? 'MF', ccNum: p?.ccNum ?? '' })} className="adm-btn adm-btn-sm adm-btn-outline">Editar</button>
                                    <button type="button" onClick={() => deleteManifestacao(m.ID)} className="adm-btn adm-btn-sm adm-btn-danger">Excluir</button>
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                        {manifestacoes.length === 0 && (
                          <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--adm-muted)', padding: '20px' }}>Nenhuma manifestação cadastrada nesta turma.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== PARTICIPANTES ===== */}
          {activeSection === 'participantes' && (
            <div className="adm-card">
              <h2 className="adm-section-title">Participantes (Respondentes)</h2>
              {turmaSelector}
              {!selectedTurma && <p style={{ color: 'var(--adm-muted)' }}>Selecione uma turma para visualizar os participantes.</p>}
              {selectedTurma && (
                <>
                  {!partForm.id && (
                    <div className="adm-form-grid">
                      <div className="adm-field">
                        <label>Nome Completo</label>
                        <input className="adm-input" placeholder="Nome do participante" value={partForm.nome} onChange={(e) => setPartForm({ ...partForm, nome: e.target.value })} />
                      </div>
                      <div className="adm-field">
                        <label>E-mail</label>
                        <input className="adm-input" placeholder="email@empresa.com" value={partForm.email} onChange={(e) => setPartForm({ ...partForm, email: e.target.value })} />
                      </div>
                      <div className="adm-field">
                        <label>Senha de Acesso</label>
                        <input
                          type="password"
                          className="adm-input"
                          placeholder="Senha individual"
                          value={partForm.senha}
                          onChange={(e) => setPartForm({ ...partForm, senha: e.target.value })}
                        />
                      </div>
                      <div className="adm-field">
                        <label>Unidade Organizacional</label>
                        <select className="adm-select" value={partForm.hierarquia} onChange={(e) => setPartForm({ ...partForm, hierarquia: e.target.value })}>
                          <option value="">Selecione unidade...</option>
                          {unidades.map(u => <option key={u.ID} value={u.ID}>{unidadeLabel(u)}</option>)}
                        </select>
                      </div>
                      {parametros.map(prm => (
                        <div className="adm-field" key={prm.id}>
                          <label>{prm.campo}</label>
                          <ParametroSelect key={(prm.valores || []).length} valores={prm.valores || []} value={partForm.parametros[prm.id] || ''}
                            onChange={(v) => setPartForm({ ...partForm, parametros: { ...partForm.parametros, [prm.id]: v } })} />
                        </div>
                      ))}
                      <button type="button" onClick={saveParticipante} className="adm-btn adm-btn-success">
                        + Adicionar Participante
                      </button>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ fontSize: '14px', color: 'var(--adm-muted)' }}>
                      Total: <strong>{participantes.length}</strong> participante(s) cadastrado(s)
                    </div>
                    {participantes.length > 0 && (
                      <button
                        type="button"
                        onClick={exportarParticipantesExcel}
                        className="adm-btn adm-btn-sm adm-btn-outline"
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        📥 Exportar Participantes (Excel)
                      </button>
                    )}
                  </div>

                  <div className="adm-table-wrapper">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th style={{ width: '60px' }}>ID</th>
                          <th>Nome</th>
                          <th>E-mail</th>
                          <th>Senha</th>
                          <th>Unidade</th>
                          {parametros.map(prm => <th key={prm.id}>{prm.campo}</th>)}
                          <th style={{ width: '170px' }} title="Última resposta ou submissão feita pelo próprio participante">Última Alteração</th>
                          <th style={{ width: '170px' }} title="Quando o participante aceitou os termos de uso">Aceite dos Termos</th>
                          <th style={{ width: '140px', textAlign: 'center' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {participantes.map(p => {
                          const unid = unidades.find(u => String(u.ID) === String(p.hierarquia))
                          const editing = partForm.id === String(p.ID)
                          if (editing) {
                            return (
                              <tr key={p.ID} className="editing">
                                <td><b>{p.ID}</b></td>
                                <td><input className="adm-input" value={partForm.nome}
                                  onChange={(e) => setPartForm({ ...partForm, nome: e.target.value })} /></td>
                                <td><input className="adm-input" value={partForm.email}
                                  onChange={(e) => setPartForm({ ...partForm, email: e.target.value })} /></td>
                                <td>
                                  <input type="text" placeholder="Senha" value={partForm.senha}
                                    onChange={(e) => setPartForm({ ...partForm, senha: e.target.value })}
                                    className="adm-input" />
                                </td>
                                <td>
                                  <select className="adm-select" value={partForm.hierarquia} onChange={(e) => setPartForm({ ...partForm, hierarquia: e.target.value })}>
                                    <option value="">Selecione unidade...</option>
                                    {unidades.map(u => <option key={u.ID} value={u.ID}>{unidadeLabel(u)}</option>)}
                                  </select>
                                </td>
                                {parametros.map(prm => (
                                  <td key={prm.id}>
                                    <ParametroSelect key={(prm.valores || []).length} valores={prm.valores || []} value={partForm.parametros[prm.id] || ''}
                                      onChange={(v) => setPartForm({ ...partForm, parametros: { ...partForm.parametros, [prm.id]: v } })} />
                                  </td>
                                ))}
                                <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtData(p.ultimaAlteracaoEm)}</td>
                                <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtData(p.aceiteTermosEm)}</td>
                                <td style={{ textAlign: 'center' }}>
                                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                    <button type="button" onClick={saveParticipante} className="adm-btn adm-btn-sm adm-btn-success">Salvar</button>
                                    <button type="button" onClick={() => setPartForm(PART_FORM_VAZIO)} className="adm-btn adm-btn-sm adm-btn-outline">Cancelar</button>
                                  </div>
                                </td>
                              </tr>
                            )
                          }
                          return (
                            <tr key={p.ID}>
                              <td><b>{p.ID}</b></td>
                              <td><strong>{p.nome}</strong></td>
                              <td>{p.email}</td>
                              <td>
                                <code style={{ fontSize: '12px', background: '#f1f3f5', padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                                  {p.senha || '—'}
                                </code>
                              </td>
                              <td>{unid ? unidadeLabel(unid) : p.hierarquia}</td>
                              {parametros.map(prm => <td key={prm.id}>{p.parametros?.[prm.id] || '—'}</td>)}
                              <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontSize: '13px', color: 'var(--adm-muted)' }}>
                                {fmtData(p.ultimaAlteracaoEm)}
                              </td>
                              <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontSize: '13px', color: p.aceiteTermosEm ? 'var(--adm-ok)' : 'var(--adm-muted)' }}>
                                {fmtData(p.aceiteTermosEm)}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                  <button type="button" onClick={() => setPartForm({ id: String(p.ID), nome: p.nome, email: p.email, senha: p.senha || '', hierarquia: String(p.hierarquia), parametros: { ...(p.parametros || {}) } })} className="adm-btn adm-btn-sm adm-btn-outline">Editar</button>
                                  <button type="button" onClick={() => deleteParticipante(p.ID)} className="adm-btn adm-btn-sm adm-btn-danger">Excluir</button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                        {participantes.length === 0 && (
                          <tr><td colSpan={7 + 1 + parametros.length} style={{ textAlign: 'center', color: 'var(--adm-muted)', padding: '20px' }}>Nenhum participante cadastrado nesta turma.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== PARÂMETROS DE FILTRO ===== */}
          {activeSection === 'parametros' && (
            <div className="adm-card">
              <h2 className="adm-section-title">Parâmetros de Filtro da Turma</h2>
              {turmaSelector}
              {!selectedTurma && <p style={{ color: 'var(--adm-muted)' }}>Selecione uma turma para configurar os parâmetros de filtro.</p>}
              {selectedTurma && (
                <>
                  {!parametroForm.id && (
                    <div className="adm-form-grid" style={{ gridTemplateColumns: '1fr auto' }}>
                      <div className="adm-field">
                        <label>Nome do Campo de Filtro</label>
                        <input className="adm-input" placeholder="Ex: Área, Tempo de Casa, Cargo"
                          value={parametroForm.campo} onChange={(e) => setParametroForm({ ...parametroForm, campo: e.target.value })} />
                      </div>
                      <button type="button" onClick={saveParametro} className="adm-btn adm-btn-success">
                        + Adicionar Parâmetro
                      </button>
                    </div>
                  )}

                  <div className="adm-table-wrapper">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th style={{ width: '60px' }}>ID</th>
                          <th>Nome do Parâmetro de Filtro</th>
                          <th style={{ width: '140px', textAlign: 'center' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parametros.map(p => {
                          const editing = parametroForm.id === String(p.id)
                          if (editing) {
                            return (
                              <tr key={p.id} className="editing">
                                <td><b>{p.id}</b></td>
                                <td>
                                  <input className="adm-input" value={parametroForm.campo}
                                    onChange={(e) => setParametroForm({ ...parametroForm, campo: e.target.value })} />
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                    <button type="button" onClick={saveParametro} className="adm-btn adm-btn-sm adm-btn-success">Salvar</button>
                                    <button type="button" onClick={() => setParametroForm({ id: '', campo: '' })} className="adm-btn adm-btn-sm adm-btn-outline">Cancelar</button>
                                  </div>
                                </td>
                              </tr>
                            )
                          }
                          return (
                            <tr key={p.id}>
                              <td><b>{p.id}</b></td>
                              <td><strong>{p.campo}</strong></td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                  <button type="button" onClick={() => setParametroForm({ id: String(p.id), campo: p.campo })} className="adm-btn adm-btn-sm adm-btn-outline">Editar</button>
                                  <button type="button" onClick={() => deleteParametro(p.id)} className="adm-btn adm-btn-sm adm-btn-danger">Excluir</button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                        {parametros.length === 0 && (
                          <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--adm-muted)', padding: '20px' }}>Nenhum parâmetro cadastrado nesta turma.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== AUDITORIA ===== */}
          {activeSection === 'auditoria' && (
            <div className="adm-card">
              <h2 className="adm-section-title">Auditoria e Registro de Ações</h2>
              <AuditoriaAdmin turmas={turmas} onError={handleApiError} />
            </div>
          )}

          {/* ===== IMPORTAR ===== */}
          {activeSection === 'import' && (() => {
            const selectedImportTurmaObj = turmas.find(t => String(t.ID) === String(importTurma))
            const dominiosImport = selectedImportTurmaObj?.dominios
              ? selectedImportTurmaObj.dominios.split(';').map(d => d.trim()).filter(Boolean)
              : []

            return (
              <div className="adm-card">
                <h2 className="adm-section-title">
                  <span>Importar Participantes em Lote (Excel)</span>
                </h2>

                {/* PASSO 1: SELEÇÃO DA TURMA */}
                <div style={{ maxWidth: '560px', marginBottom: '20px' }}>
                  <div className="adm-field">
                    <label style={{ fontWeight: 800, fontSize: '13.5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      1. Turma de Destino
                    </label>
                    <select 
                      className="adm-select" 
                      value={importTurma} 
                      onChange={(e) => {
                        setImportTurma(e.target.value)
                        setImportResult(null)
                      }}
                    >
                      <option value="">Selecione uma turma...</option>
                      {turmas.map(t => (
                        <option key={t.ID} value={t.ID}>
                          (Código: {t.ID}) {t.nome.trim()}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!importTurma && (
                    <p style={{ fontSize: '12.5px', color: 'var(--adm-muted)', marginTop: '4px' }}>
                      Selecione a turma para visualizar o padrão exato de colunas e parâmetros exigidos.
                    </p>
                  )}
                </div>

                {importTurma && (
                  <>
                    {/* PASSO 2: GUIA VISUAL DO PADRÃO DA PLANILHA */}
                    <div style={{
                      background: 'var(--adm-page)',
                      border: '1.5px solid var(--adm-line)',
                      borderRadius: '10px',
                      padding: '16px 18px',
                      marginBottom: '22px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
                        <div>
                          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--adm-navy)' }}>
                            2. Padrão Exigido para a Planilha Excel (.xlsx / .xls)
                          </h3>
                          <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: 'var(--adm-muted)' }}>
                            A 1ª linha deve conter os cabeçalhos. O sistema aceita letras maiúsculas ou minúsculas (ex: <em>Nome</em> ou <em>nome</em>).
                          </p>
                        </div>

                        <button 
                          type="button" 
                          onClick={downloadModelo} 
                          className="adm-btn adm-btn-outline" 
                          style={{ fontWeight: 700, borderColor: 'var(--adm-navy)', color: 'var(--adm-navy)', background: '#fff' }}
                        >
                          ⬇ Baixar Planilha Modelo (.xlsx) Desta Turma
                        </button>
                      </div>

                      {/* Tabela de Colunas */}
                      <div style={{ overflowX: 'auto', border: '1px solid var(--adm-line)', borderRadius: '8px', background: '#fff' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: 'var(--adm-navy-soft)', textAlign: 'left', color: 'var(--adm-navy)' }}>
                              <th style={{ padding: '8px 12px', borderBottom: '1px solid var(--adm-line)', fontWeight: 800 }}>Coluna</th>
                              <th style={{ padding: '8px 12px', borderBottom: '1px solid var(--adm-line)', fontWeight: 800 }}>Tipo / Regra</th>
                              <th style={{ padding: '8px 12px', borderBottom: '1px solid var(--adm-line)', fontWeight: 800 }}>Status</th>
                              <th style={{ padding: '8px 12px', borderBottom: '1px solid var(--adm-line)', fontWeight: 800 }}>Exemplo</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={{ padding: '9px 12px', fontWeight: 700, color: 'var(--adm-navy)', borderBottom: '1px solid #f1f5f9' }}>
                                Nome
                              </td>
                              <td style={{ padding: '9px 12px', color: 'var(--adm-ink)', borderBottom: '1px solid #f1f5f9' }}>
                                Nome completo do participante
                              </td>
                              <td style={{ padding: '9px 12px', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{ background: 'var(--adm-err-soft)', color: 'var(--adm-err)', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, fontSize: '11px' }}>
                                  OBRIGATÓRIO
                                </span>
                              </td>
                              <td style={{ padding: '9px 12px', color: 'var(--adm-muted)', fontStyle: 'italic', borderBottom: '1px solid #f1f5f9' }}>
                                Maria Santos
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: '9px 12px', fontWeight: 700, color: 'var(--adm-navy)', borderBottom: '1px solid #f1f5f9' }}>
                                Login
                              </td>
                              <td style={{ padding: '9px 12px', color: 'var(--adm-ink)', borderBottom: '1px solid #f1f5f9' }}>
                                Identificador que o participante digita para entrar: e-mail, CPF, matrícula ou outro código.
                                Para CPF ou números com zero à esquerda, formate a coluna como Texto no Excel.
                                {dominiosImport.length > 0 && (
                                  <div style={{ marginTop: '4px' }}>
                                    <span style={{ fontSize: '11px', color: 'var(--adm-muted)', display: 'block' }}>Se o login for um e-mail, domínios aceitos nesta turma:</span>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px' }}>
                                      {dominiosImport.map(d => (
                                        <span key={d} style={{ background: '#e0e7ff', color: '#3730a3', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 700 }}>
                                          @{d.replace(/^@/, '')}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '9px 12px', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{ background: 'var(--adm-err-soft)', color: 'var(--adm-err)', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, fontSize: '11px' }}>
                                  OBRIGATÓRIO
                                </span>
                              </td>
                              <td style={{ padding: '9px 12px', color: 'var(--adm-muted)', fontStyle: 'italic', borderBottom: '1px solid #f1f5f9' }}>
                                {dominiosImport.length > 0 ? `maria.santos@${dominiosImport[0].replace(/^@/, '')}` : 'maria.santos@empresa.com'} ou 01234567890
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: '9px 12px', fontWeight: 700, color: 'var(--adm-navy)', borderBottom: '1px solid #f1f5f9' }}>
                                Hierarquia
                              </td>
                              <td style={{ padding: '9px 12px', color: 'var(--adm-ink)', borderBottom: '1px solid #f1f5f9' }}>
                                Nome do nível (ex: <em>Diretoria</em>, <em>Gerência</em>) ou número do nível/ID
                                {importUnidades.length > 0 && (
                                  <div style={{ marginTop: '4px' }}>
                                    <span style={{ fontSize: '11px', color: 'var(--adm-muted)', display: 'block' }}>Níveis cadastrados:</span>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px' }}>
                                      {importUnidades.map(u => (
                                        <span key={u.ID} style={{ background: '#f1f5f9', color: '#334155', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', border: '1px solid var(--adm-line)' }}>
                                          {unidadeLabel(u)}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '9px 12px', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{ background: '#f3f4f6', color: '#4b5563', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, fontSize: '11px' }}>
                                  OPCIONAL
                                </span>
                              </td>
                              <td style={{ padding: '9px 12px', color: 'var(--adm-muted)', fontStyle: 'italic', borderBottom: '1px solid #f1f5f9' }}>
                                {importUnidades.length > 0 ? importUnidades[0].nome : 'Diretoria'}
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: '9px 12px', fontWeight: 700, color: 'var(--adm-navy)', borderBottom: importParametros.length > 0 ? '1px solid #f1f5f9' : 'none' }}>
                                Senha
                              </td>
                              <td style={{ padding: '9px 12px', color: 'var(--adm-ink)', borderBottom: importParametros.length > 0 ? '1px solid #f1f5f9' : 'none' }}>
                                Senha opcional. Se vazia, o participante entra normalmente via link/e-mail
                              </td>
                              <td style={{ padding: '9px 12px', borderBottom: importParametros.length > 0 ? '1px solid #f1f5f9' : 'none' }}>
                                <span style={{ background: '#f3f4f6', color: '#4b5563', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, fontSize: '11px' }}>
                                  OPCIONAL
                                </span>
                              </td>
                              <td style={{ padding: '9px 12px', color: 'var(--adm-muted)', fontStyle: 'italic', borderBottom: importParametros.length > 0 ? '1px solid #f1f5f9' : 'none' }}>
                                (deixar em branco)
                              </td>
                            </tr>

                            {/* PARÂMETROS ESPECÍFICOS DA TURMA */}
                            {importParametros.map((p, idx) => (
                              <tr key={p.id}>
                                <td style={{ padding: '9px 12px', fontWeight: 700, color: 'var(--adm-navy)', borderBottom: idx === importParametros.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                                  {p.campo}
                                </td>
                                <td style={{ padding: '9px 12px', color: 'var(--adm-ink)', borderBottom: idx === importParametros.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                                  Filtro da turma (ex: Área, Cargo, Unidade, Filial). Usado no debriefing.
                                </td>
                                <td style={{ padding: '9px 12px', borderBottom: idx === importParametros.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                                  <span style={{ background: 'var(--adm-warn-soft)', color: 'var(--adm-warn)', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, fontSize: '11px' }}>
                                    PARÂMETRO TURMA
                                  </span>
                                </td>
                                <td style={{ padding: '9px 12px', color: 'var(--adm-muted)', fontStyle: 'italic', borderBottom: idx === importParametros.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                                  Comercial
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* PASSO 3: SELEÇÃO DO ARQUIVO & PRÉ-VISUALIZAÇÃO */}
                    <div style={{
                      background: '#ffffff',
                      border: '1.5px solid var(--adm-line)',
                      borderRadius: '10px',
                      padding: '16px 18px',
                      marginBottom: '22px'
                    }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: '1.05rem', fontWeight: 800, color: 'var(--adm-navy)' }}>
                        3. Selecione o Arquivo Excel (.xlsx ou .xls)
                      </h3>

                      <input 
                        type="file" 
                        accept=".xlsx,.xls" 
                        onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                        className="adm-input" 
                        style={{ padding: '10px', minHeight: '46px', cursor: 'pointer', background: 'var(--adm-page)' }} 
                      />

                      {/* PRÉ-VISUALIZAÇÃO EM TEMPO REAL */}
                      {filePreview && (
                        <div style={{ marginTop: '16px', background: 'var(--adm-page)', border: '1px solid var(--adm-line)', borderRadius: '8px', padding: '14px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                            <div>
                              <strong style={{ fontSize: '14px', color: 'var(--adm-navy)' }}>📄 {filePreview.fileName}</strong>
                              <span style={{ fontSize: '12px', color: 'var(--adm-muted)', marginLeft: '8px' }}>({filePreview.fileSize})</span>
                            </div>
                            <span style={{ background: 'var(--adm-navy-soft)', color: 'var(--adm-navy)', padding: '3px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: 700 }}>
                              {filePreview.totalRows} participantes detectados
                            </span>
                          </div>

                          {/* Validação de Compatibilidade */}
                          {filePreview.isValid ? (
                            <div style={{ background: 'var(--adm-ok-soft)', color: 'var(--adm-ok)', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                              <span>✓</span> Estrutura compatível! Todas as colunas obrigatórias foram localizadas na planilha.
                            </div>
                          ) : (
                            <div style={{ background: 'var(--adm-err-soft)', color: 'var(--adm-err)', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>
                              <div style={{ fontWeight: 700, marginBottom: '4px' }}>⚠️ Incompatibilidade detectada na planilha:</div>
                              {filePreview.missingRequired.length > 0 && (
                                <div>• Coluna(s) obrigatória(s) ausente(s): <strong>{filePreview.missingRequired.join(', ')}</strong></div>
                              )}
                              {filePreview.missingParams.length > 0 && (
                                <div>• Parâmetro(s) da turma ausente(s): <strong>{filePreview.missingParams.join(', ')}</strong></div>
                              )}
                              <div style={{ marginTop: '6px', fontSize: '12px' }}>
                                Dica: Baixe a planilha modelo no passo 2 acima para obter exatamente as colunas configuradas.
                              </div>
                            </div>
                          )}

                          {/* Tags de Colunas Encontradas */}
                          <div style={{ marginBottom: '12px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--adm-muted)' }}>Colunas identificadas na planilha: </span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                              {filePreview.headersFound.map(h => (
                                <span key={h} style={{ background: '#ffffff', border: '1px solid var(--adm-line)', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}>
                                  {h}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Amostra das primeiras linhas */}
                          {filePreview.sampleRows.length > 0 && (
                            <div>
                              <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--adm-muted)' }}>Prévia dos primeiros registros:</span>
                              <div style={{ overflowX: 'auto', marginTop: '6px', border: '1px solid var(--adm-line)', borderRadius: '6px', background: '#fff' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                  <thead>
                                    <tr style={{ background: 'var(--adm-navy-soft)', color: 'var(--adm-navy)' }}>
                                      {filePreview.headersFound.slice(0, 6).map(h => (
                                        <th key={h} style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid var(--adm-line)' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filePreview.sampleRows.map((r, i) => (
                                      <tr key={i}>
                                        {filePreview.headersFound.slice(0, 6).map(h => (
                                          <td key={h} style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>{String(r[h] ?? '')}</td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* BOTÃO DISPARADOR DE IMPORTAÇÃO */}
                      <div style={{ marginTop: '16px' }}>
                        <button 
                          type="button" 
                          onClick={handleImport} 
                          disabled={importing || !importFile || !importTurma || (filePreview ? !filePreview.isValid : false)}
                          className="adm-btn adm-btn-success"
                          style={{ minHeight: '48px', fontSize: '1rem', width: '100%', fontWeight: 700 }}
                        >
                          {importing ? 'Importando participantes...' : '▶ Iniciar Importação em Lote'}
                        </button>
                      </div>
                    </div>

                    {/* PASSO 4: RESUMO DETALHADO DO RESULTADO */}
                    {importResult && (
                      <div style={{
                        background: '#ffffff',
                        border: `2px solid ${importResult.success === false ? 'var(--adm-err)' : 'var(--adm-ok)'}`,
                        borderRadius: '10px',
                        padding: '20px',
                        marginBottom: '20px',
                        boxShadow: importResult.success === false ? '0 4px 14px rgba(153, 27, 27, 0.12)' : '0 4px 14px rgba(22, 101, 52, 0.12)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                          <span style={{ fontSize: '26px' }}>{importResult.success === false ? '⚠️' : '🎉'}</span>
                          <div>
                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: importResult.success === false ? 'var(--adm-err)' : 'var(--adm-ok)' }}>
                              {importResult.message}
                            </h3>
                            <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--adm-muted)' }}>
                              Processamento da turma de código <strong>{importResult.turma}</strong>
                            </p>
                          </div>
                        </div>

                        {/* Grade de Métricas */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                          <div style={{ background: 'var(--adm-page)', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--adm-navy)' }}>{importResult.totalLidos}</div>
                            <div style={{ fontSize: '12px', color: 'var(--adm-muted)' }}>Linhas Lidas</div>
                          </div>
                          <div style={{ background: 'var(--adm-ok-soft)', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--adm-ok)' }}>{importResult.criados}</div>
                            <div style={{ fontSize: '12px', color: 'var(--adm-ok)' }}>Novos Cadastrados</div>
                          </div>
                          <div style={{ background: 'var(--adm-navy-soft)', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--adm-navy)' }}>{importResult.atualizados}</div>
                            <div style={{ fontSize: '12px', color: 'var(--adm-navy)' }}>Atualizados (Existentes)</div>
                          </div>
                          <div style={{ background: importResult.erros?.length ? 'var(--adm-warn-soft)' : '#f8fafc', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '22px', fontWeight: 800, color: importResult.erros?.length ? 'var(--adm-warn)' : 'var(--adm-muted)' }}>
                              {importResult.erros?.length || 0}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--adm-muted)' }}>Linhas com Avisos</div>
                          </div>
                        </div>

                        {/* Tabela de Linhas Ignoradas/Erros se houver */}
                        {importResult.erros && importResult.erros.length > 0 && (
                          <div style={{ marginBottom: '16px', border: '1px solid var(--adm-warn)', borderRadius: '8px', padding: '12px 14px', background: 'var(--adm-warn-soft)' }}>
                            <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--adm-warn)', marginBottom: '8px' }}>
                              ⚠️ Linhas que não puderam ser importadas:
                            </div>
                            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                                    <th style={{ padding: '4px 8px' }}>Linha</th>
                                    <th style={{ padding: '4px 8px' }}>Login</th>
                                    <th style={{ padding: '4px 8px' }}>Motivo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {importResult.erros.map((err, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                      <td style={{ padding: '4px 8px', fontWeight: 700 }}>{err.linha}</td>
                                      <td style={{ padding: '4px 8px' }}>{err.email}</td>
                                      <td style={{ padding: '4px 8px' }}>{err.motivo}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Botões de Ação Final */}
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                          <button 
                            type="button" 
                            onClick={() => {
                              setSelectedTurma(importTurma)
                              setActiveSection('participantes')
                            }} 
                            className="adm-btn adm-btn-primary" 
                            style={{ fontWeight: 700 }}
                          >
                            👥 Ver Lista de Participantes Desta Turma →
                          </button>

                          <button 
                            type="button" 
                            onClick={() => {
                              setImportFile(null)
                              setFilePreview(null)
                              setImportResult(null)
                            }} 
                            className="adm-btn adm-btn-outline"
                          >
                            Importar Outro Arquivo
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })()}

        </main>
      </div>

      {toast && (
        <div role="status" style={{
          position: 'fixed', 
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', 
          right: 'clamp(12px, 3vw, 24px)',
          backgroundColor: toastError ? 'var(--adm-err)' : 'var(--adm-navy)',
          color: '#ffffff', 
          padding: '12px 18px', 
          borderRadius: '8px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.25)', 
          zIndex: 1000,
          maxWidth: 'min(420px, calc(100% - 24px))', 
          lineHeight: '1.4', 
          fontWeight: 600, 
          fontSize: '14px',
          boxSizing: 'border-box'
        }}>
          {toastError ? '⚠ ' : '✓ '}{toast}
        </div>
      )}
    </div>
  )
}
